import { type ReactNode, type Dispatch, type SetStateAction, type ReactElement } from 'react'
import AppIcon from '@/components/content/app/AppIcon'
import { CriteriaOverviewContent, type CriterionWithRating } from '@/components/ui/CriteriaOverview'
import { getBrandColors, isPopulatedObject, scrollToSection } from '@/lib/utils'
import { formatDeal } from '@/components/content/deals/helpers'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import { processAffiliateLink } from '@/lib/formatting'
import { createLogger } from '@/lib/logger'
import type { App, Deal, Media } from '@/payload-types'
import type { ResultItemProps, ResultType } from '@/types/app'

const logger = createLogger('menus')

export type MenuItem = {
  label: ReactNode | string
  icon: ReactNode | string
  sub?: MenuItem[]
  subTitle?: ReactNode | string
  onClick?: () => void
  simultaneousClick?: () => void
  link?: string
  passReferrerUrl?: boolean
  sponsored?: boolean
  selected?: boolean
  disabled?: boolean
  back?: boolean
  custom?: ReactNode
}

export type MenuType = 'app' | 'apps' | 'deal' | 'compare' | 'chat'

type AppBrandColorFields = Pick<
  App,
  'brandBackgroundColor' | 'brandPrimaryColor' | 'brandSecondaryColor' | 'brandTertiaryColor'
>

type MenuApp = Pick<App, 'id' | 'name' | 'slug' | 'affiliateLink'> &
  Partial<
    Pick<App, 'icon' | 'glyph' | 'deals' | 'categoryRecommendations' | 'partnershipTier'> &
      AppBrandColorFields
  >

type CompareWithApp = Pick<App, 'id' | 'slug'> & Partial<Pick<App, 'name'>>

type AppMenuParams = {
  app: MenuApp | MenuApp[]
  compareWithApp?: CompareWithApp
  // Canonical stored comparison slug for (app, compareWithApp). When provided, the
  // compare link uses it verbatim so it points at the pre-built /compare page and
  // avoids the non-canonical-order 307 redirect.
  canonicalCompareSlug?: string
  // When the app belongs to a broad category (e.g. AI), the compare link points
  // to the single-app picker (/compare/<slug>) instead of a fixed pair.
  isBroadCategory?: boolean
  criteria?: CriterionWithRating[]
  deals?: Deal[]
  video?: boolean
  course?: string
  pathname?: string
  isReview?: boolean
  setPlayVideo?: (play: boolean) => void
  resetTab?: () => void
}

type DealMenuParams = {
  app: App
  deals?: Deal[]
  selectedDeal?: Deal
  setSelectedDeal?: (deal: Deal) => void
}

type CompareMenuParams = {
  apps: App[]
  setApps?: Dispatch<SetStateAction<App[]>>
  relatedApps?: App[]
  comparison?: App[]
  // Replaces the all-apps Deals/Visit Site/Read Review submenus with this one app's deal
  // action and direct links; the comparison-wide swap/add/share items are unchanged.
  focusApp?: App
  options?: {
    isSelectable?: boolean
    isComparable?: boolean
    isSwappable?: boolean
    isAppendable?: boolean
    isShareable?: boolean
  }
}

type ChatMenuParams = {
  apps: App[]
  shortcuts: ResultItemProps[]
  handleShortcutClick: (collection: ResultType) => void
  addApp: (app: App) => void
}

type MenuParamsByType = {
  app: AppMenuParams
  apps: AppMenuParams
  deal: DealMenuParams
  compare: CompareMenuParams
  chat: ChatMenuParams
}

function createAppIcon({
  app,
  customIcon,
  customGlyph,
  customName,
}: {
  app: Pick<App, 'name'> & Partial<Pick<App, 'icon' | 'glyph'> & AppBrandColorFields>
  customIcon?: App['icon']
  customGlyph?: App['glyph']
  customName?: string
}): ReactElement | null {
  // Icon falls back to app.icon when no customIcon is passed; glyph is optional.
  // Bail only when the icon can't resolve to a Media object (missing or an
  // unpopulated id) — undefined customIcon must NOT bail (that's the fallback case).
  const resolvedIcon = customIcon ?? app.icon
  const resolvedGlyph = customGlyph ?? app.glyph

  if (!isPopulatedObject<Media>(resolvedIcon) && !isPopulatedObject<Media>(resolvedGlyph)) {
    return null
  }

  const { backgroundColor } = getBrandColors({
    brandBackgroundColor: app.brandBackgroundColor,
    brandPrimaryColor: app.brandPrimaryColor ?? '#000000',
    brandSecondaryColor: app.brandSecondaryColor,
    brandTertiaryColor: app.brandTertiaryColor,
  })
  return (
    <AppIcon
      icon={resolvedIcon}
      glyph={isPopulatedObject<Media>(resolvedGlyph) ? resolvedGlyph : undefined}
      name={customName || app.name}
      background={backgroundColor}
      size={20}
    />
  )
}

function getCriteriaOverviewApp(
  app: MenuApp,
): Parameters<typeof CriteriaOverviewContent>[0]['app'] {
  return {
    id: app.id,
    name: app.name,
    slug: app.slug,
    icon: app.icon,
    glyph: app.glyph,
    brandBackgroundColor: app.brandBackgroundColor,
    brandPrimaryColor: app.brandPrimaryColor ?? '#000000',
    categoryRecommendations: app.categoryRecommendations,
  }
}

// Helper function to format deal menu items
function formatDealMenuItems(
  deals: Deal[],
  appSlug?: App['slug'],
  icon?: ReactNode | string,
): MenuItem[] {
  return deals.map((deal) => {
    const formattedDeal = formatDeal(deal)
    return {
      label: formattedDeal.button,
      icon: icon ?? formattedDeal.icon,
      link: `/deals/${appSlug}?claim=true`,
      onClick: () => {
        localStorage.setItem('efficient:deal_id', deal.id.toString())
      },
    }
  })
}

// Unified menu generation function with type-safe params per menu type
export function generateMenu<T extends MenuType>(type: T, params: MenuParamsByType[T]): MenuItem[] {
  switch (type) {
    case 'app':
    case 'apps':
      return generateAppMenuLogic(params as AppMenuParams)
    case 'deal':
      return generateDealMenuLogic(params as DealMenuParams)
    case 'compare':
      return generateCompareMenuLogic(params as CompareMenuParams)
    case 'chat':
      return generateChatMenuLogic(params as ChatMenuParams)
    default:
      return []
  }
}

// Type-specific logic functions
function generateAppMenuLogic(params: AppMenuParams): MenuItem[] {
  const {
    app,
    compareWithApp,
    canonicalCompareSlug,
    isBroadCategory,
    criteria,
    deals,
    video,
    pathname,
    isReview,
    setPlayVideo,
    resetTab,
  } = params
  const appArray = Array.isArray(app) ? app : [app]
  const isSingleApp = appArray.length === 1
  const singleApp = isSingleApp ? appArray[0] : null

  // Single app logic (with optional comparison)
  if (isSingleApp && singleApp) {
    const canCompare = compareWithApp && singleApp.slug && compareWithApp.slug

    const dealsToDisplay = deals?.length ? determineDealsToDisplay(deals) : []
    const singleDeal = dealsToDisplay.length === 1 ? formatDeal(dealsToDisplay[0]) : null
    const dealsMenuItem: MenuItem | null = dealsToDisplay.length
      ? singleDeal
        ? {
            label: 'Deal Details',
            icon: singleDeal.icon,
            link: `/deals/${singleApp.slug}`,
          }
        : {
            label: `Deals`,
            icon: 'deal',
            sub: formatDealMenuItems(dealsToDisplay, singleApp.slug ?? undefined),
          }
      : null

    const baseMenu: MenuItem[] = [
      ...(params.course
        ? [
            {
              label: 'Access Course',
              icon: 'graduation-cap',
              link: params.course,
            },
            {
              label: 'Divider',
              icon: <></>,
            },
          ]
        : []),
      ...(isBroadCategory && singleApp.slug
        ? [
            {
              label: `Compare`,
              icon: 'comparison',
              link: `/compare/${singleApp.slug}`,
            },
            {
              label: 'Divider',
              icon: <></>,
            },
          ]
        : canCompare
          ? [
              {
                label: compareWithApp?.name
                  ? `Compare with ${compareWithApp.name}`
                  : 'Compare with top pick',
                icon: 'comparison',
                // Top pick first: the stored canonical order is almost always
                // <topPick>-vs-<app>, so this avoids the 307 redirect when no
                // canonical slug was resolved. canonicalCompareSlug (when present)
                // is exact and takes precedence.
                link: canonicalCompareSlug
                  ? `/compare/${canonicalCompareSlug}`
                  : `/compare/${compareWithApp.slug}-vs-${singleApp.slug}`,
              },
              {
                label: 'Divider',
                icon: <></>,
              },
            ]
          : []),
      ...(singleApp.slug
        ? [
            ...(!isReview
              ? [
                  {
                    label: `Read Review`,
                    icon: 'review',
                    link: `/apps/${singleApp.slug}`,
                  },
                ]
              : []),
            ...(dealsMenuItem ? [dealsMenuItem] : []),
            (() => {
              const affiliate = processAffiliateLink(
                singleApp.affiliateLink,
                singleApp.slug,
                singleApp.name,
                singleApp.partnershipTier,
              )
              return {
                label: `Visit Site`,
                icon: 'site',
                link: affiliate.url,
                passReferrerUrl: affiliate.passReferrerUrl,
                sponsored: affiliate.isSponsored,
              }
            })(),
          ]
        : []),
      ...(criteria?.length
        ? [
            {
              label: `Efficiency Score`,
              icon: 'gauge',
              custom: (
                <CriteriaOverviewContent
                  app={getCriteriaOverviewApp(singleApp)}
                  criteria={criteria}
                  hideDescription={true}
                />
              ),
            },
          ]
        : []),
      ...(setPlayVideo
        ? [
            {
              label: `Watch Video`,
              icon: 'youtube',
              onClick: async () => {
                resetTab?.()
                await scrollToSection({
                  id: 'video-player',
                  offset: 150,
                  animate: true,
                  hideHash: true,
                })
                setPlayVideo(true)
              },
            },
          ]
        : video
          ? [
              {
                label: `Watch Video`,
                icon: 'youtube',
                onClick: () => {
                  resetTab?.()
                  scrollToSection({
                    id: 'video-player',
                    offset: 150,
                    animate: true,
                    hideHash: true,
                  })
                },
              },
            ]
          : []),
      ...(isReview
        ? [
            {
              label: `Share`,
              icon: 'share',
              link: `${typeof window !== 'undefined' ? window.location.pathname : ''}?utm_medium=share`,
            },
          ]
        : []),
    ]

    return baseMenu
  }

  // Multiple apps logic
  const shareLink = pathname
  const multipleApps = appArray.length > 1

  const { dealsMenu, reviewMenu, siteMenu } = appArray.reduce(
    (acc, currentApp) => {
      const appDeals = currentApp.deals?.docs?.filter(isPopulatedObject<Deal>) ?? []
      const appIcon = (customIcon?: App['icon'], customGlyph?: App['glyph'], customName?: string) =>
        createAppIcon({
          app: currentApp,
          customIcon,
          customGlyph,
          customName,
        })

      if (appDeals?.length) {
        const dealsToDisplay = determineDealsToDisplay(appDeals)
        const singleDeal = dealsToDisplay.length === 1 ? formatDeal(dealsToDisplay[0]) : null

        if (multipleApps && !singleDeal) {
          const dealItems = formatDealMenuItems(dealsToDisplay, currentApp.slug, appIcon())
          acc.dealsMenu.push(...dealItems)
        } else {
          acc.dealsMenu.push(
            singleDeal
              ? {
                  label: singleDeal.button,
                  icon: appIcon(),
                  link: `/deals/${currentApp.slug}?claim=true`,
                }
              : {
                  label: `Deals`,
                  icon: 'ticket',
                  sub: formatDealMenuItems(dealsToDisplay, currentApp.slug),
                },
          )
        }
      }

      acc.reviewMenu.push({
        label: multipleApps ? currentApp.name : `Read Review`,
        icon: multipleApps ? appIcon() : 'review',
        link: `/apps/${currentApp.slug}`,
      })

      const siteAffiliate = processAffiliateLink(
        currentApp.affiliateLink,
        currentApp.slug,
        currentApp.name,
        currentApp.partnershipTier,
      )
      acc.siteMenu.push({
        label: multipleApps ? currentApp.name : `Visit Site`,
        icon: multipleApps ? appIcon() : 'site',
        link: siteAffiliate.url,
        passReferrerUrl: siteAffiliate.passReferrerUrl,
        sponsored: siteAffiliate.isSponsored,
      })

      return acc
    },
    {
      dealsMenu: [] as MenuItem[],
      reviewMenu: [] as MenuItem[],
      siteMenu: [] as MenuItem[],
    },
  )

  return [
    ...(dealsMenu.length === 1 && !('sub' in dealsMenu[0])
      ? dealsMenu
      : dealsMenu.length
        ? [
            {
              label: `Deals`,
              icon: 'ticket',
              sub: dealsMenu,
            },
          ]
        : []),
    ...(siteMenu.length
      ? [
          {
            label: `Visit Site`,
            icon: 'site',
            sub: siteMenu,
          },
        ]
      : []),
    ...(reviewMenu.length
      ? [
          {
            label: `Read Review`,
            icon: 'review',
            sub: reviewMenu,
          },
        ]
      : []),
    {
      label: `Share`,
      icon: 'share',
      link: shareLink,
    },
  ]
}

function generateDealMenuLogic(params: DealMenuParams): MenuItem[] {
  const { app, deals, selectedDeal, setSelectedDeal } = params

  if (!app) {
    logger.error('generateDealMenuLogic App is undefined')
    return []
  }

  const dealsMenu =
    deals &&
    deals.length > 0 &&
    deals.map((deal) => {
      const formattedDeal = formatDeal(deal)
      return {
        label: formattedDeal.button,
        icon: formattedDeal.icon,
        link: `/deals/${app.slug}`,
        onClick: setSelectedDeal ? () => setSelectedDeal(deal) : undefined,
        simultaneousClick: () => localStorage.setItem('efficient:deal_id', deal.id.toString()),
        selected: deal.id === selectedDeal?.id,
      }
    })

  const hasMultipleDeals = deals && deals.length > 1
  const shareLink = hasMultipleDeals
    ? `/deals/${app.slug}?id=${selectedDeal?.id}&utm_medium=share`
    : `/deals/${app.slug}?utm_medium=share`

  return [
    ...(dealsMenu && dealsMenu.length > 1
      ? [
          {
            label: `Deals`,
            icon: 'deal',
            sub: dealsMenu,
          },
        ]
      : []),
    {
      label: `Read Review`,
      link: `/apps/${app.slug}`,
      icon: 'review',
    },
    (() => {
      const affiliate = processAffiliateLink(
        app.affiliateLink,
        app.slug,
        app.name,
        app.partnershipTier,
      )
      return {
        label: `Visit Site`,
        link: affiliate.url,
        passReferrerUrl: affiliate.passReferrerUrl,
        sponsored: affiliate.isSponsored,
        icon: 'site',
      }
    })(),
    {
      label: `Share`,
      icon: 'share',
      link: shareLink,
    },
  ]
}

function generateCompareMenuLogic(params: CompareMenuParams): MenuItem[] {
  const { apps, setApps, relatedApps, focusApp, options = {}, comparison = [] } = params
  const appArray = Array.isArray(apps) ? apps : [apps]
  const multipleApps = appArray.length > 1
  const comparisonLength = comparison?.length || 2

  const { selectMenu, compareMenu, appendMenu, dealsMenu, reviewMenu, siteMenu } = appArray.reduce(
    (acc, app) => {
      if (!app.slug) return acc

      const appIcon = (customIcon?: App['icon'], customGlyph?: App['glyph'], customName?: string) =>
        createAppIcon({
          app,
          customIcon,
          customGlyph,
          customName,
        })

      const swapMenuItems = (relatedApps ?? [])
        .map((related) => {
          const relatedIcon = createAppIcon({
            app: related,
            customIcon: related.icon,
            customGlyph: related.glyph,
            customName: related.name,
          })
          if (!relatedIcon) return null

          return {
            label: related.name,
            icon: relatedIcon,
            onClick: setApps
              ? () =>
                  setApps(
                    (comparison.length > 0 ? comparison : appArray).map((a) =>
                      a.id === app.id ? related : a,
                    ),
                  )
              : undefined,
            disabled: (comparison.length > 0 ? comparison : appArray).some(
              (a) => a.id === related.id,
            ),
          } as MenuItem
        })
        .filter((item): item is MenuItem => item !== null)

      acc.selectMenu.push(
        {
          label: `Swap app for comparison`,
          icon: 'swap',
          sub: swapMenuItems,
        },
        ...(comparisonLength > 2
          ? [
              {
                label: `Remove ${app.name}`,
                icon: 'remove',
                onClick: setApps
                  ? () => setApps((prevApps: App[]) => prevApps.filter((a: App) => a.id !== app.id))
                  : undefined,
              },
            ]
          : []),
        {
          label: 'Divider',
          icon: <></>,
        },
      )

      if (options.isComparable) {
        const compareMenuItems = relatedApps
          ?.filter((a) => a.id !== app.id)
          .map((related) => {
            if (!related.slug) return null
            const relatedIcon = createAppIcon({
              app: related,
              customIcon: related.icon,
              customGlyph: related.glyph,
              customName: related.name,
            })
            if (!relatedIcon) return null

            return {
              label: related.name,
              icon: relatedIcon,
              link: `/compare/${app?.slug}-vs-${related.slug}`,
            } as MenuItem
          })
          .filter((item): item is MenuItem => item !== null)

        acc.compareMenu.push(
          {
            label: `Compare ${app.name} with…`,
            icon: 'comparison',
            sub: compareMenuItems,
          },
          { label: 'Divider', icon: <></> },
        )
      }

      if (options.isAppendable) {
        acc.appendMenu.push({
          label: `Add ${app.name} to this comparison`,
          icon: 'add-item',
          onClick: setApps ? () => setApps((prev) => [...prev, app]) : undefined,
        })
      }

      const dealItem = appDealsMenuItem(app, multipleApps ? appIcon() : undefined)
      if (dealItem) acc.dealsMenu.push(dealItem)

      acc.reviewMenu.push({
        label: multipleApps ? app.name : `Read Review`,
        icon: multipleApps ? appIcon() : 'review',
        link: `/apps/${app.slug}`,
      })

      const altSiteAffiliate = processAffiliateLink(
        app.affiliateLink,
        app.slug,
        app.name,
        app.partnershipTier,
      )
      acc.siteMenu.push({
        label: multipleApps ? app.name : `Visit Site`,
        icon: multipleApps ? appIcon() : 'site',
        link: altSiteAffiliate.url,
        passReferrerUrl: altSiteAffiliate.passReferrerUrl,
        sponsored: altSiteAffiliate.isSponsored,
      })

      return acc
    },
    {
      selectMenu: [] as MenuItem[],
      compareMenu: [] as MenuItem[],
      appendMenu: [] as MenuItem[],
      dealsMenu: [] as MenuItem[],
      reviewMenu: [] as MenuItem[],
      siteMenu: [] as MenuItem[],
    },
  )

  if (!multipleApps) {
    return [
      ...(options.isSelectable ? selectMenu : []),
      ...(options.isAppendable ? appendMenu : []),
      ...(compareMenu.length ? compareMenu : []),
      ...(dealsMenu.length ? dealsMenu : []),
      ...reviewMenu,
      ...siteMenu,
    ]
  }

  const shareLink = `/compare/${appArray
    .map((app) => app.slug)
    .filter(Boolean)
    .join('-vs-')}`

  // Create nested swap menu for multiple apps
  const swapAppMenu: MenuItem[] = appArray.map((app) => {
    const appIcon = createAppIcon({
      app,
      customIcon: app.icon,
      customGlyph: app.glyph,
      customName: app.name,
    })

    const swapOptionsForApp = (relatedApps ?? [])
      .map((related) => {
        const relatedIcon = createAppIcon({
          app: related,
          customIcon: related.icon,
          customGlyph: related.glyph,
          customName: related.name,
        })
        if (!relatedIcon) return null

        return {
          label: related.name,
          icon: relatedIcon,
          onClick: setApps
            ? () =>
                setApps(
                  (comparison.length > 0 ? comparison : appArray).map((a) =>
                    a.id === app.id ? related : a,
                  ),
                )
            : undefined,
          disabled: (comparison.length > 0 ? comparison : appArray).some(
            (a) => a.id === related.id,
          ),
        } as MenuItem
      })
      .filter((item): item is MenuItem => item !== null)

    return {
      label: app.name,
      icon: appIcon,
      sub: swapOptionsForApp,
      subTitle: (
        <p>
          Swap <span className="text-black">{app.name}</span> for comparison...
        </p>
      ),
    }
  })

  // Create nested remove menu for multiple apps
  const removeAppMenu: MenuItem[] = appArray.map((app) => {
    const appIcon = createAppIcon({
      app,
      customIcon: app.icon,
      customGlyph: app.glyph,
      customName: app.name,
    })

    return {
      label: app.name,
      icon: appIcon,
      onClick: setApps
        ? () => setApps((prevApps: App[]) => prevApps.filter((a: App) => a.id !== app.id))
        : undefined,
    }
  })

  return [
    ...(options.isSwappable
      ? [
          {
            label: `Swap app for comparison`,
            icon: 'swap',
            sub: swapAppMenu,
            subTitle: 'Swap app for comparison...',
          },
          ...(appArray.length > 2
            ? [
                {
                  label: `Remove app from comparison`,
                  icon: 'remove',
                  sub: removeAppMenu,
                  subTitle: 'Remove app from comparison...',
                },
              ]
            : []),
          ...(options.isSelectable ? [] : [{ label: 'Divider', icon: <></> }]),
        ]
      : []),
    ...(options.isSelectable && appArray.length < 4
      ? [
          {
            label: `Add app for comparison`,
            icon: 'add-item',
            sub: (relatedApps ?? [])
              .map((related) => {
                const relatedIcon = createAppIcon({
                  app: related,
                  customIcon: related.icon,
                  customGlyph: related.glyph,
                  customName: related.name,
                })
                if (!relatedIcon) return null

                return {
                  label: related.name,
                  icon: relatedIcon,
                  onClick: setApps ? () => setApps([...appArray, related]) : undefined,
                  disabled: appArray.some((a) => a.id === related.id),
                } as MenuItem
              })
              .filter((item): item is MenuItem => item !== null),
          },
          {
            label: 'Divider',
            icon: <></>,
          },
        ]
      : []),
    ...(focusApp?.slug
      ? focusAppMenu(focusApp)
      : [
          ...(dealsMenu.length === 1 && !('sub' in dealsMenu[0])
            ? dealsMenu
            : dealsMenu.length
              ? [{ label: `Deals`, icon: 'deal', sub: dealsMenu }]
              : []),
          ...(siteMenu.length
            ? [
                {
                  label: `Visit Site`,
                  icon: 'site',
                  sub: siteMenu,
                },
              ]
            : []),
          ...(reviewMenu.length
            ? [
                {
                  label: `Read Review`,
                  icon: 'review',
                  sub: reviewMenu,
                },
              ]
            : []),
        ]),
    ...(options.isShareable
      ? [
          {
            label: `Share`,
            icon: 'share',
            link: shareLink,
          },
        ]
      : []),
  ]
}

// One app's deal action: a direct claim link for a single displayed deal, a Deals submenu
// for several, or nothing when none are currently displayable. Pass `appIcon` when the
// item sits in a list of several apps, to label it with the app instead of "Deals".
function appDealsMenuItem(app: App, appIcon?: ReactNode): MenuItem | null {
  const deals = app.deals?.docs?.filter(isPopulatedObject<Deal>) ?? []
  const dealsToDisplay = deals.length ? determineDealsToDisplay(deals) : []
  if (!dealsToDisplay.length) return null
  if (dealsToDisplay.length === 1) {
    const singleDeal = formatDeal(dealsToDisplay[0])
    return {
      label: singleDeal.button,
      icon: appIcon ?? singleDeal.icon,
      link: `/deals/${app.slug}?claim=true`,
    }
  }
  return {
    label: appIcon ? app.name : `Deals`,
    icon: appIcon ?? 'deal',
    sub: formatDealMenuItems(dealsToDisplay, app.slug ?? undefined),
  }
}

function focusAppMenu(app: App): MenuItem[] {
  const affiliate = processAffiliateLink(app.affiliateLink, app.slug, app.name, app.partnershipTier)
  const dealItem = appDealsMenuItem(app)
  return [
    ...(dealItem ? [dealItem] : []),
    {
      label: `Visit Site`,
      icon: 'site',
      link: affiliate.url,
      passReferrerUrl: affiliate.passReferrerUrl,
      sponsored: affiliate.isSponsored,
    },
    {
      label: `Read Review`,
      icon: 'review',
      link: `/apps/${app.slug}`,
    },
  ]
}

function generateChatMenuLogic(params: ChatMenuParams): MenuItem[] {
  const { apps, shortcuts, handleShortcutClick, addApp } = params

  if (!apps) {
    logger.error('generateChatMenuLogic - Apps is undefined')
    return []
  }

  const menu: MenuItem[] = [
    {
      label: `Apps`,
      icon: 'window',
      sub: (apps ?? [])
        .map((app) => {
          const relatedIcon = createAppIcon({
            app,
            customIcon: app.icon,
            customGlyph: app.glyph,
            customName: app.name,
          })
          if (!relatedIcon) return null

          return {
            label: app.name,
            icon: relatedIcon,
            onClick: () => addApp(app),
          } as MenuItem
        })
        .filter((item): item is MenuItem => item !== null),
    },
    {
      label: `Shortcuts`,
      icon: 'shortcut',
      sub: shortcuts.map((shortcut) => {
        return {
          label: shortcut.label,
          icon: shortcut.icon,
          onClick: () => handleShortcutClick(shortcut.type),
        } as MenuItem
      }),
    },
  ]

  return menu
}
